import { AgGrid } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DefaultColumnTypes } from "#utils/agGrid.constants";
import Money from "../../../../Money";
import { TextWithIcon } from "../../../../TextWithIcon";
import { TextWithTag } from "../../../../TextWithTag";
import {
  INVALID_PAYMENT_INSTRUCTIONS_TOOLTIP_TEXT,
  PAYMENT_INSTRUCTIONS_UPDATED_TAG_TEXT,
  PAYMENT_INSTRUCTIONS_UPDATED_TOOLTIP_TEXT,
  PositionTabStatus,
} from "./constants";
import { PositionRowData } from "./types";
import { getPaymentMethodDisplay } from "./utils";

// Makes the cell itself a flex container so that AG Grid's `.ag-react-container`
// wrapper (which hosts our React cell renderer) is vertically centered. This
// works reliably across browsers, unlike `align-content` on the cell.
const verticalCenterStyle = { display: "flex", alignItems: "center", overflow: "hidden" };

/**
 * List of the supported columns in the Select Position table
 * (ordered from left to right)
 */
export enum SupportedColumns {
  AccountName = "accountName",
  PaymentInstructions = "paymentInstructions",
  PaymentMethod = "paymentMethod",
  PaymentMethodStatus = "paymentMethodStatus",
  NetPaymentAmount = "netPaymentAmount",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.AccountName, "Account name"],
  [SupportedColumns.PaymentInstructions, "Payment instructions"],
  [SupportedColumns.PaymentMethod, "Payment method"],
  [SupportedColumns.PaymentMethodStatus, "Payment method status"],
  [SupportedColumns.NetPaymentAmount, "Net payment amount"],
]);

export const getColumnDefinitions = (
  allowSelection: boolean,
  showStatusColumn: boolean,
): AgGrid.ColDef[] => [
  {
    field: SupportedColumns.AccountName,
    headerName: columnNames.get(SupportedColumns.AccountName),
    pinned: "left" as const,
    minWidth: 360,
    checkboxSelection: allowSelection,
    headerCheckboxSelection: allowSelection,
    cellStyle: verticalCenterStyle,
    cellRenderer: TextWithTag,
    cellRendererParams: ({ data }: { data: PositionRowData }) => {
      if (
        data.paymentInstructionsRecentlyUpdated &&
        data.positionTabStatus !== PositionTabStatus.Paid
      ) {
        return {
          label: data.accountName,
          tagLabel: PAYMENT_INSTRUCTIONS_UPDATED_TAG_TEXT,
          tagVariant: "warning",
        };
      }

      return {
        label: data.accountName,
      };
    },
    tooltipValueGetter: ({ data }: { data?: PositionRowData }) => {
      if (
        data?.paymentInstructionsRecentlyUpdated &&
        data?.positionTabStatus !== PositionTabStatus.Paid
      ) {
        return PAYMENT_INSTRUCTIONS_UPDATED_TOOLTIP_TEXT;
      }

      return "";
    },
  },
  {
    field: SupportedColumns.PaymentInstructions,
    headerName: columnNames.get(SupportedColumns.PaymentInstructions),
    width: 360,
  },
  {
    field: SupportedColumns.PaymentMethod,
    headerName: columnNames.get(SupportedColumns.PaymentMethod),
    maxWidth: 200,
    valueFormatter: ({ data }: { data: PositionRowData }) => {
      return getPaymentMethodDisplay(data.paymentMethod);
    },
  },
  {
    field: SupportedColumns.PaymentMethodStatus,
    headerName: columnNames.get(SupportedColumns.PaymentMethodStatus),
    hide: !showStatusColumn,
    minWidth: 300,
    cellStyle: verticalCenterStyle,
    cellRenderer: TextWithIcon,
    cellRendererParams: ({ data }: { data: PositionRowData }) => {
      return {
        label: data.unableToPayReason?.unableToPayReason || "",
        iconType: "exclamation-triangle",
        iconColor: Colors.Danger600,
      };
    },
    tooltipValueGetter: ({ data }: { data?: PositionRowData }) => {
      const invalidPaymentInstructionReason =
        data?.unableToPayReason?.invalidPaymentInstructionReason;
      return invalidPaymentInstructionReason
        ? INVALID_PAYMENT_INSTRUCTIONS_TOOLTIP_TEXT[invalidPaymentInstructionReason]
        : "";
    },
    lockVisible: true, // Prevents users from hiding/unhiding this column in UI
    // We don't want users to be able to show this column in the Available to Pay or Paid tables
  },
  {
    field: SupportedColumns.NetPaymentAmount,
    headerName: columnNames.get(SupportedColumns.NetPaymentAmount),
    type: DefaultColumnTypes.numericColumn,
    maxWidth: 200,
    sortable: false,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: PositionRowData }) => {
      return {
        money: data.netPayment,
        rounded: false,
      };
    },
  },
];
